import type { Prayer } from "./types";

export const prayer: Prayer = {
  slug: "prayer-for-patience",
  title: "A prayer for patience — for waiting that has gone on",
  h1: "A prayer for patience",
  description:
    "A prayer for the wait that has outlasted your patience, four passages on what the word actually means, and the joke about praying for it.",
  targets: [
    "prayer for patience",
    "prayer while waiting",
    "prayer for patience with people",
    "prayer when nothing is happening",
  ],
  published: "2026-09-03",
  standfirst:
    "The old joke is that praying for patience gets you a difficult year. It is funnier than it is true, but only just.",
  takeaways: [
    "Two different words are translated patience: one for waiting out circumstances, one for putting up with people. Work out which you are short of before you ask.",
    "James 5:7 uses a farmer as the model — someone who cannot make anything happen faster and knows it, which is the part that stings.",
    "Patience is not the same as passivity. Galatians 6:9 assumes you are still doing the work while nothing visible comes back.",
  ],
  when: [
    "Month seven of an application, a treatment, a hiring process, a wait.",
    "With the same person, about the same thing, for the fourth time this week.",
    "When somebody who started after you has already arrived.",
    "In traffic, in a queue, on hold — the small ones that show you the size of the problem.",
  ],
  prayer: [
    "God, I am out of patience and I have started to take it out on people who are not responsible for any of this.",
    "Nothing is moving. I have done what I can do and the rest is not mine, and knowing that has not helped at all.",
    "Give me something to do while I wait that is not checking. And take the specific bitterness I am developing towards the people who got what I am waiting for.",
    "If this is going to be a long one, tell me. I would rather know it is years than keep expecting Thursday.",
    "Make me patient with the people in front of me, which is the part I keep failing while I concentrate on the waiting.",
  ],
  short:
    "God, nothing is moving. Give me something to do that is not checking.",
  honest: [
    "This prayer will not make the wait shorter, and it does not claim the delay is a lesson. Scripture is comfortable with waits that were simply long, with no explanation attached afterwards.",
    "It also does not ask you to stop wanting the thing. Patience in the New Testament is a way of holding on, not a way of caring less — the farmer in James is still watching the field every day.",
    "And it is not an instruction to accept mistreatment. Being long-tempered with people is a virtue; staying somewhere that is damaging you because leaving would be impatient is not what the word means.",
  ],
  verses: [
    {
      verse: {
        text: "Be patient therefore, brothers, until the coming of the Lord. Behold, the farmer waits for the precious fruit of the earth, being patient over it, until it receives the early and late rain. You also be patient. Establish your hearts, for the coming of the Lord is at hand.",
        ref: "James 5:7–8",
      },
      note: "The farmer is chosen precisely because he has no leverage. He can plant and he can wait, and the rain arrives on its own schedule or it does not. The instruction that follows is not to relax but to establish your hearts — to brace, which is a strange and useful thing to be told while waiting.",
    },
    {
      verse: {
        text: "Wait for Yahweh. Be strong, and let your heart take courage. Yes, wait for Yahweh.",
        ref: "Psalm 27:14",
      },
      note: "The repetition is not decoration; it is the psalm telling itself the same thing twice because once did not take. Courage is the word attached to waiting here, which is a better match for how it actually feels than patience is — waiting is frightening, and the psalm treats it that way.",
    },
    {
      verse: {
        text: "Let's not be weary in doing good, for we will reap in due season if we don't give up.",
        ref: "Galatians 6:9",
      },
      note: "Note that the person addressed is still working. This is not a verse about sitting still; it is about continuing to do the unrewarded thing during the stretch where nothing visible comes back. In due season is deliberately unspecified, and that vagueness is the hard part rather than a translation problem.",
    },
    {
      verse: {
        text: "rejoicing in hope, enduring in troubles, continuing steadfastly in prayer,",
        ref: "Romans 12:12",
      },
      note: "Three participles that describe one posture, and the middle one is the honest one — enduring, which assumes the trouble is still happening. The last is the practical instruction: keep praying about it, steadily, rather than praying hard for a fortnight and then going quiet when nothing changes.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "Two kinds of patience",
      id: "two-kinds-of-patience",
    },
    {
      type: "p",
      text: "New Testament Greek distinguishes between putting up with circumstances and putting up with people. English collapses both into one word, which is why the prayer often misses: somebody exhausted by a slow process asks for the strength to endure it, when what has actually run out is the ability to be gentle with their family in the meantime.",
    },
    {
      type: "p",
      text: "Work out which you are short of. The second one is more urgent, because the circumstances cannot be hurt by your impatience and the people can.",
    },
    {
      type: "h2",
      text: "Give the waiting something to do",
      id: "give-the-waiting-something-to-do",
    },
    {
      type: "p",
      text: "The reason a wait becomes unbearable is usually not its length but its emptiness. There is nothing to do, so the mind fills the space with checking — the inbox, the tracker, the feed, the same page refreshed for the ninth time.",
    },
    {
      type: "p",
      text: "That checking is not neutral. It converts a wait into a series of small disappointments, several times an hour, and it is worth {putting a wall} between yourself and the thing you keep refreshing.",
      links: {
        "putting a wall": "/blog/screen-time-limits-dont-work",
      },
    },
    {
      type: "h2",
      text: "The joke, examined",
      id: "the-joke-examined",
    },
    {
      type: "p",
      text: "Do not pray for patience or God will give you something to be patient about. It is funny because it assumes a God who teaches by imposing hardship on request, which is not how any of the passages above describe the process.",
    },
    {
      type: "note",
      text: "What the texts actually describe is patience produced by going through something you were already in. Nobody in Scripture is handed a trial because they asked to be improved.",
    },
  ],
  faq: [
    {
      q: "What is a good prayer for patience?",
      a: 'Ask for an occupation rather than a feeling: "God, nothing is moving — give me something to do that is not checking." The emptiness of a wait is usually harder than its length.',
    },
    {
      q: "Which Bible verse is about patience?",
      a: "James 5:7–8, for the farmer who has no way to hurry the rain, and Galatians 6:9 for the stretch where you are still working and nothing visible is coming back.",
    },
    {
      q: "Is it true you should not pray for patience?",
      a: "No. The joke assumes God teaches by arranging hardship on request, which is not how Scripture describes it. Patience in the New Testament is produced inside things people were already going through.",
    },
  ],
  relatedPrayers: [
    "prayer-for-strength",
    "prayer-for-anger",
    "prayer-for-guidance",
  ],
  related: ["digital-sabbath", "screen-time-limits-dont-work"],
  relatedTopics: ["bible-verses-about-time", "bible-verses-about-strength"],
  relatedPassages: ["jeremiah-29-11"],
};
